import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Avatar, Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
// utils
import { fDate } from '../../../utils/formatTime';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
// utils
import { DEFAULT_BLOG_AVATAR, DEFAULT_BLOG_COVER } from '../../../utils/blog';

// ----------------------------------------------------------------------

const StyledExcerpt = styled(Typography)({
  overflow: 'hidden',
  WebkitLineClamp: 3,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
});

const StyledTitle = styled(Typography)({
  overflow: 'hidden',
  WebkitLineClamp: 2,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
});

// ----------------------------------------------------------------------

BlogFeaturedCard.propTypes = {
  post: PropTypes.object.isRequired,
};

export default function BlogFeaturedCard({ post }) {
  const { slug, cover, title, excerpt, category, readTime, author, createdAt } = post ?? {};
  const authorName = author?.name ?? 'ASAP UAE';

  return (
    <MotionInView variants={varFadeInUp} sx={{ mb: { xs: 3, md: 5 } }}>
      <Card
        component={RouterLink}
        to={`/blog/${slug}`}
        aria-label={`Read featured article: ${title}`}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          borderRadius: 2,
          overflow: 'hidden',
          color: 'inherit',
          textDecoration: 'none',
          transition: 'transform 0.4s cubic-bezier(0.43, 0.13, 0.23, 0.96), box-shadow 0.4s ease',
          '&:focus-visible': {
            outline: (theme) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 4,
          },
          '@media (hover: hover)': {
            '&:hover': {
              transform: 'translateY(-6px)',
              boxShadow: (theme) => `0 22px 44px ${alpha(theme.palette.grey[900], 0.18)}`,
            },
            '&:hover img': { transform: 'scale(1.05)' },
            '&:hover .read-arrow': { transform: 'translateX(4px)' },
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            flex: { md: '0 0 50%' },
            minHeight: { xs: 220, sm: 320, md: 420 },
            overflow: 'hidden',
            bgcolor: 'grey.200',
            '& img': {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.6s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
            },
          }}
        >
          <Box component="img" alt={title} src={cover} loading="lazy" decoding="async" onError={(event) => { event.currentTarget.src = DEFAULT_BLOG_COVER; }} />

          <Chip
            label="Featured"
            size="small"
            sx={{
              position: 'absolute',
              top: { xs: 16, md: 20 },
              left: { xs: 16, md: 20 },
              height: 24,
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              bgcolor: '#DF3E30',
              color: 'common.white',
            }}
          />
        </Box>

        <CardContent
          sx={{
            flex: 1,
            p: { xs: 3, md: 4 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'center' }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 0.6 }}>
              {category}
            </Typography>
          </Stack>

          <StyledTitle variant="h3" sx={{ color: 'text.primary', mb: 2 }}>
            {title}
          </StyledTitle>

          <StyledExcerpt variant="body1" sx={{ color: 'text.secondary', flexGrow: 1 }}>
            {excerpt}
          </StyledExcerpt>

          <Stack
            direction="row"
            spacing={2}
            sx={{ mt: 3, flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Stack direction="row" spacing={2} sx={{ minWidth: 0, alignItems: 'center' }}>
              <Avatar
                alt={authorName}
                src={author?.avatarUrl}
                imgProps={{ onError: (event) => { event.currentTarget.src = DEFAULT_BLOG_AVATAR; } }}
                sx={{ width: 44, height: 44 }}
              >
                {authorName.charAt(0)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                  {authorName}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  {fDate(createdAt)} · {readTime} min read
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.5} sx={{ color: 'primary.main', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
                Read article
              </Typography>
              <Iconify
                className="read-arrow"
                icon="eva:arrow-forward-outline"
                sx={{ width: 18, height: 18, transition: 'transform 0.25s ease' }}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </MotionInView>
  );
}
